import { useState, useEffect } from "react";
import { useUser } from "../contextapi/UserContext";
import { supabase } from "../supabase/supabaseClient";

interface AdminAuthState {
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useAdminAuth = () => {
  const { user } = useUser();
  const [adminState, setAdminState] = useState<AdminAuthState>({
    isAdmin: false,
    isLoading: true,
    error: null,
  });
  const [checkedUserId, setCheckedUserId] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setAdminState({
          isAdmin: false,
          isLoading: false,
          error: null,
        });
        setCheckedUserId(null);
        return;
      }

      // Only check if we haven't checked this user before or if user changed
      if (checkedUserId === user.id) {
        return;
      }

      try {
        setAdminState((prev) => ({ ...prev, isLoading: true, error: null }));

        const { data, error } = await supabase
          .from("base_profiles")
          .select("role")
          .eq("id", user.id)
          .single();

        if (error) {
          throw error;
        }

        const isAdmin = data?.role === "admin";

        setAdminState({
          isAdmin,
          isLoading: false,
          error: null,
        });
        setCheckedUserId(user.id);
      } catch (error) {
        console.error("Error checking admin role:", error);
        setAdminState({
          isAdmin: false,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : "Failed to check admin role",
        });
        setCheckedUserId(user.id); // Still mark as checked to prevent infinite loops
      }
    };

    checkAdminRole();
  }, [user, checkedUserId]);

  return adminState;
};
